import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  errorMessage,
  useCapacity,
  useCreateJob,
  useEnvironments,
  useJobs,
} from "../api/queries";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { PageHeader } from "../components/layout/PageHeader";
import { Field } from "../components/ui/Field";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import {
  familyIsUnknown,
  familyLabel,
  gpuCountLabel,
  gpusPerNode,
  workloadFamilyLabel,
} from "../lib/capacity";
import { formatElapsed, pluralize } from "../lib/format";
import { validateJobForm } from "../lib/jobs";
import type { JobFormErrors, JobFormValues } from "../lib/jobs";
import { jobStateLabel, jobTone } from "../lib/workload";

const EMPTY_FORM: JobFormValues = {
  name: "",
  image: "",
  command: "",
  gpus: 1,
  hours: 4,
};

/**
 * Jobs: submit a command, come back for the log.
 *
 * Returns:
 *     The jobs screen — deliberately plain, workspaces are the product.
 */
export function JobsPage(): JSX.Element {
  const navigate = useNavigate();
  const { me } = useSession();
  const jobs = useJobs({ mine: true, limit: 50 });
  const environments = useEnvironments();
  const capacity = useCapacity();
  const create = useCreateJob();

  const [values, setValues] = useState<JobFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<JobFormErrors>({});
  const [family, setFamily] = useState<string | null>(null);

  const environmentOptions = environments.data?.items ?? [];
  const image = values.image || environmentOptions[0]?.id || "";

  // Which GPU, then how many (ADR-0020) — the same question the workspace form
  // asks, and the same rule for answering it: an explicit pick is sent
  // whatever the fleet publishes afterwards, and a fleet with one kind of GPU
  // is never asked and submits exactly what it did before families existed.
  const families = capacity.data?.families ?? [];
  const selectedFamily =
    family ?? capacity.data?.defaults.gpu_family ?? families[0]?.family ?? null;
  const requestedFamily = family ?? (families.length > 1 ? selectedFamily : null);
  const familyEntry = families.find((entry) => entry.family === requestedFamily);
  const unreadableFamily = capacity.data
    ? familyIsUnknown(capacity.data, requestedFamily)
    : false;
  // The cap is the chosen family's biggest machine, never the fleet's: the
  // other GPU's 8-GPU machine is not room for a job that asked for this one.
  const maxGpus = requestedFamily
    ? Math.max(1, ...(familyEntry?.allowed_gpu_counts ?? []))
    : capacity.data
      ? gpusPerNode(capacity.data)
      : 1;

  const update = <K extends keyof JobFormValues>(
    key: K,
    value: JobFormValues[K],
  ): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const submit = (): void => {
    const candidate: JobFormValues = { ...values, image };
    const found = validateJobForm(candidate);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    create.mutate(
      {
        name: candidate.name.trim(),
        image: candidate.image,
        command: candidate.command.trim(),
        gpus: candidate.gpus,
        hours: candidate.hours,
        ...(requestedFamily ? { gpu_family: requestedFamily } : {}),
      },
      {
        onSuccess: (job) => {
          setValues(EMPTY_FORM);
          navigate(`/jobs/${job.id}`);
        },
      },
    );
  };

  if (isClusterProblem(jobs.error)) {
    return (
      <>
        <PageHeader
          eyebrow="Batch"
          title="Jobs"
          subtitle="Run a command, read the log."
        />
        <ClusterProblem error={jobs.error} onRetry={() => void jobs.refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Batch"
        title="Jobs"
        subtitle="Run a command on GPUs and come back for the log."
      />

      <section className="section" aria-labelledby="submit-job-title">
        <h2 className="section__title" id="submit-job-title">
          Submit a job
        </h2>
        <form
          className="job-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field label="Name" htmlFor="job-name" error={errors.name}>
            <input
              id="job-name"
              className="input"
              value={values.name}
              onChange={(event) => update("name", event.target.value)}
            />
          </Field>

          <Field label="Environment" htmlFor="job-image" error={errors.image}>
            <select
              id="job-image"
              className="select"
              value={image}
              onChange={(event) => update("image", event.target.value)}
            >
              {environmentOptions.length === 0 ? (
                <option value="">No environment published</option>
              ) : null}
              {environmentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.added_by
                    ? `${option.name} — added by ${option.added_by}`
                    : option.name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Command"
            htmlFor="job-command"
            error={errors.command}
            hint={`Runs in /home/${me.username} — the same files as your workspace. Anything it needs (code, data, environments) must already be there.`}
          >
            <textarea
              id="job-command"
              className="textarea textarea--mono"
              value={values.command}
              spellCheck={false}
              onChange={(event) => update("command", event.target.value)}
            />
          </Field>

          {families.length > 1 ? (
            <fieldset className="form__block">
              <legend className="form__legend">Which GPU?</legend>
              <div className="gpu-choices">
                {families.map((entry) => {
                  const biggest = Math.max(0, ...entry.allowed_gpu_counts);
                  return (
                    <label
                      key={entry.family}
                      className="gpu-choice"
                      data-selected={selectedFamily === entry.family}
                    >
                      <input
                        type="radio"
                        name="job-gpu-family"
                        className="sr-only"
                        value={entry.family}
                        checked={selectedFamily === entry.family}
                        aria-label={familyLabel(entry.family)}
                        aria-describedby={`job-gpu-family-${entry.family}`}
                        onChange={() => {
                          setFamily(entry.family);
                          // A count this family cannot hold is not a count:
                          // back to one, which every family offers.
                          update("gpus", Math.min(values.gpus, biggest || 1));
                        }}
                      />
                      <span
                        className="gpu-choice__count gpu-choice__count--word"
                        aria-hidden="true"
                      >
                        {familyLabel(entry.family)}
                      </span>
                      <span
                        className="gpu-choice__reason"
                        id={`job-gpu-family-${entry.family}`}
                      >
                        {biggest > 0
                          ? `Up to ${pluralize(biggest, "GPU")} on one machine — ${entry.gpu_free} free right now.`
                          : `${entry.gpu_free} free right now.`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          {unreadableFamily && requestedFamily ? (
            <p className="form__error" role="status">
              The console cannot say what {familyLabel(requestedFamily)}{" "}
              machines hold right now, so it cannot say how many GPUs you may
              ask for. It retries by itself — try again in a minute.
            </p>
          ) : null}

          <div className="job-form__row">
            <Field label="GPUs" htmlFor="job-gpus" error={errors.gpus}>
              <input
                id="job-gpus"
                className="input input--narrow"
                type="number"
                min={1}
                max={maxGpus}
                value={values.gpus}
                onChange={(event) => update("gpus", Number(event.target.value))}
              />
            </Field>
            <Field label="Hours" htmlFor="job-hours" error={errors.hours}>
              <input
                id="job-hours"
                className="input input--narrow"
                type="number"
                min={1}
                value={values.hours}
                onChange={(event) => update("hours", Number(event.target.value))}
              />
            </Field>
          </div>

          {create.isError ? (
            <p className="form__error" role="alert">
              {errorMessage(create.error)}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn btn--primary"
            disabled={create.isPending}
          >
            {create.isPending ? "Submitting…" : "Submit"}
          </button>
        </form>
      </section>

      <section className="section" aria-labelledby="job-list-title">
        <h2 className="section__title" id="job-list-title">
          Your jobs
        </h2>
        {jobs.isPending ? (
          <LoadingBlock label="Loading your jobs…" />
        ) : jobs.isError ? (
          <ErrorBlock error={jobs.error} onRetry={() => void jobs.refetch()} />
        ) : jobs.data.items.length === 0 ? (
          <EmptyBlock label="You have not submitted any job yet." />
        ) : (
          <ul className="job-list">
            {jobs.data.items.map((job) => (
              <li key={job.id} className="job-row">
                <Link className="job-row__name mono" to={`/jobs/${job.id}`}>
                  {job.name}
                </Link>
                <span className="badge" data-tone={jobTone(job.state)}>
                  <span className="badge__dot" aria-hidden="true" />
                  {jobStateLabel(job.state)}
                </span>
                <span className="job-row__meta">
                  {gpuCountLabel(
                    job.gpus,
                    workloadFamilyLabel(capacity.data, job.gpu_family),
                  )}{" "}
                  · started{" "}
                  {job.started_at ? `${formatElapsed(job.started_at)} ago` : "not yet"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
